import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
// Disabled portal-based components to avoid removeChild errors
// import {
//   DropdownMenu,
//   DropdownMenuContent,
//   DropdownMenuItem,
//   DropdownMenuTrigger,
//   DropdownMenuSeparator,
// } from '../components/ui/dropdown-menu';
// import {
//   Dialog,
//   DialogContent,
//   DialogDescription,
//   DialogFooter,
//   DialogHeader,
//   DialogTitle,
// } from '../components/ui/dialog';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Calendar, Clock, Phone, User, LogOut, Plus, Send, Edit, Trash2, FileText, MoreVertical, CheckCircle, XCircle, AlertCircle, Tag as TagIcon, RefreshCw, SendHorizontal, X, Bell, Building2, UserPlus, ChevronDown, ChevronUp, BarChart3, Calendar as CalendarIcon, Heart } from 'lucide-react';
import { appointmentsAPI, profileAPI, branchesAPI, servicesAPI } from '../services/api';
import AppointmentForm from '../components/AppointmentForm';
import NotificationBell from '../components/NotificationBell';
import NotificationSettings from '../components/NotificationSettings';
import PatientSearch from '../components/PatientSearch';
import UserProfile from '../components/UserProfile';
import RecommendationsManager from '../components/RecommendationsManager';
import WhatsAppMessageEditor from '../components/WhatsAppMessageEditor';
import PatientTagsManager from '../components/PatientTagsManager';
import TagSelector from '../components/TagSelector';
import PushNotificationManager from '../components/PushNotificationManager';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
// import { toast } from '../hooks/use-toast'; // Disabled to avoid portal errors

// Simple toast replacement
const toast = ({ title, description, variant }) => {
  console.log(`[${variant === 'destructive' ? 'ERROR' : 'INFO'}] ${title}: ${description}`);
};
import { useAutoReminders } from '../hooks/useAutoReminders';
import { checkUpcomingAppointments, sendNotification, requestNotificationPermission } from '../utils/notifications';
import { APPOINTMENT_STATUSES, STORAGE_KEYS, DEFAULT_TAGS } from '../mock';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const Dashboard = () => {
  const { currentUser, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [appointments, setAppointments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showProfile, setShowProfile] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [selectedAppointmentForRec, setSelectedAppointmentForRec] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [showWhatsAppEditor, setShowWhatsAppEditor] = useState(false);
  const [selectedAppointmentForWA, setSelectedAppointmentForWA] = useState(null);
  const [showTagsManager, setShowTagsManager] = useState(false);
  const [showTagSelector, setShowTagSelector] = useState(false);
  const [selectedAppointmentForTags, setSelectedAppointmentForTags] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [showMassReminderDialog, setShowMassReminderDialog] = useState(false);
  const [selectedDateForReminders, setSelectedDateForReminders] = useState(new Date());
  const [showPendingRemindersDialog, setShowPendingRemindersDialog] = useState(false);
  const [onlySelectedBranch, setOnlySelectedBranch] = useState(true); // Para recordatorios masivos
  const [massRemindersEnabled, setMassRemindersEnabled] = useState(() => {
    return localStorage.getItem('massRemindersEnabled') !== 'false';
  });
  
  // Branches (Sucursales) - now from API
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  
  // Services
  const [services, setServices] = useState([]);
  
  // Confirmation dialog
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [newlyCreatedAppointment, setNewlyCreatedAppointment] = useState(null);
  const [expandedCards, setExpandedCards] = useState(new Set()); // Estado para controlar tarjetas expandidas
  const [openMenuId, setOpenMenuId] = useState(null); // Para controlar qué menú está abierto
  
  // Subscription and trial states
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [userSettings, setUserSettings] = useState(null);
  
  // Auto reminders hook
  const { pendingReminders, clearPendingReminders } = useAutoReminders(appointments);

  // Check subscription status and user settings
  useEffect(() => {
    const checkSubscriptionAndSettings = async () => {
      if (!currentUser) return;
      
      try {
        // Get subscription status
        const subResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/subscription-status`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('notimax_token')}`
          }
        });
        
        if (subResponse.ok) {
          const data = await subResponse.json();
          setSubscriptionStatus(data);
          
          // Calculate days remaining
          if (data.current_period_end) {
            const endDate = new Date(data.current_period_end);
            const now = new Date();
            const diffTime = endDate - now;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            setDaysRemaining(diffDays);
          } else if (data.plan === 'trial') {
            // Default trial is 14 days
            setDaysRemaining(14);
          }
        }

        // Get user settings (including show_upgrade_banner)
        const userResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('notimax_token')}`
          }
        });
        
        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUserSettings(userData);
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
      }
    };
    
    checkSubscriptionAndSettings();
  }, [currentUser]);

  // Check if should show banner (respecting user preference)
  const shouldShowBanner = () => {
    if (!subscriptionStatus || currentUser?.role === 'admin') return false;
    if (subscriptionStatus.is_premium) return false;
    
    // Check if user has banner disabled (default is true, so undefined means show it)
    // Use userSettings first (most updated), fallback to currentUser
    const showBanner = userSettings?.show_upgrade_banner ?? currentUser?.show_upgrade_banner;
    if (showBanner === false) return false;
    
    return true;
  };

  useEffect(() => {
    // Don't redirect while still loading auth state
    if (loading) {
      return;
    }
    
    if (!currentUser) {
      navigate('/login');
      return;
    }
    loadData();
    requestNotificationPermission();
    
    // Check if we're navigating from calendar with an appointment to edit
    if (location.state?.editAppointment) {
      setEditingAppointment(location.state.editAppointment);
      setShowForm(true);
      // Clear the state to avoid reopening on refresh
      window.history.replaceState({}, document.title);
    }
    
    // Check if we're navigating from calendar to create new appointment
    if (location.state?.createAppointment && location.state?.preselectedDate) {
      setEditingAppointment({ date: location.state.preselectedDate });
      setShowForm(true);
      // Clear the state to avoid reopening on refresh
      window.history.replaceState({}, document.title);
    }

    // Reload appointments when user returns to the page
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('Page visible, reloading appointments...');
        loadAppointments();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also reload every 30 seconds while page is active
    const interval = setInterval(() => {
      if (!document.hidden) {
        loadAppointments();
      }
    }, 30000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, [currentUser, loading, navigate, location]);

  const loadData = async () => {
    setDataLoading(true);
    try {
      await Promise.all([loadAppointments(), loadUserProfile(), loadBranches(), loadServices()]);
      loadTags();
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        variant: 'destructive',
      });
    } finally {
      setDataLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const response = await branchesAPI.getAll();
      setBranches(response.data);
    } catch (error) {
      console.error('Error loading branches:', error);
      // Don't show error toast as branches are optional
    }
  };

  const loadServices = async () => {
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data);
    } catch (error) {
      console.error('Error loading services:', error);
      // Don't show error toast as services are optional
    }
  };

  const loadTags = async () => {
    try {
      const response = await profileAPI.get();
      const profileData = response.data;
      const loadedTags = profileData.patientTags || DEFAULT_TAGS;
      setAvailableTags(loadedTags);
    } catch (error) {
      console.error('Error loading tags:', error);
      setAvailableTags(DEFAULT_TAGS);
    }
  };

  const loadUserProfile = async () => {
    try {
      const response = await profileAPI.get();
      setUserProfile(response.data);
    } catch (error) {
      console.error('Error loading profile:', error);
      // Set default profile from currentUser
      setUserProfile({
        name: currentUser?.name || '',
        email: currentUser?.email || '',
        businessName: '',
        phone: '',
        photoUrl: '',
        speciality: ''
      });
    }
  };

  const getUserInitials = () => {
    const name = userProfile?.name || currentUser?.name || 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  useEffect(() => {
    // Revisar citas próximas cada minuto
    const checkNotifications = () => {
      // Verificar si las notificaciones están disponibles
      if (!('Notification' in window)) {
        return; // Salir si no están disponibles
      }
      
      const upcoming = checkUpcomingAppointments(appointments);
      upcoming.forEach(apt => {
        const aptId = apt._id || apt.id;
        const notificationKey = `notified_${aptId}_${apt.date}_${apt.time}_${apt.reminderTime}`;
        const alreadyNotified = localStorage.getItem(notificationKey);
        
        // Get reminder label
        const reminderMinutes = apt.reminderTime || 120;
        let timeLabel = '';
        if (reminderMinutes < 60) {
          timeLabel = `${reminderMinutes} minutos`;
        } else {
          const hours = reminderMinutes / 60;
          timeLabel = `${hours} hora${hours > 1 ? 's' : ''}`;
        }
        
        if (!alreadyNotified && apt.minutesDiff <= reminderMinutes) {
          sendNotification(`🔔 Recordatorio de cita: ${apt.patientName}`, {
            body: `${apt.time} - ${apt.reason}\n${apt.serviceType ? '📋 ' + apt.serviceType : ''}`,
            icon: '/favicon.ico',
            tag: aptId,
            requireInteraction: true
          });
          localStorage.setItem(notificationKey, 'true');
        }
      });
    };

    checkNotifications();
    const interval = setInterval(checkNotifications, 60 * 1000); // cada 1 minuto
    return () => clearInterval(interval);
  }, [appointments]);

  const loadAppointments = async () => {
    try {
      const response = await appointmentsAPI.getAll();
      const sortedAppointments = response.data.sort((a, b) => 
        new Date(a.date + ' ' + a.time) - new Date(b.date + ' ' + b.time)
      );
      setAppointments(sortedAppointments);
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las citas',
        variant: 'destructive',
      });
    }
  };

  const handleBranchChange = (branchId) => {
    setSelectedBranch(branchId);
    localStorage.setItem('selectedBranch', branchId);
    toast({
      title: 'Sucursal cambiada',
      description: `Mostrando citas de ${branches.find(b => b.id === branchId)?.name || 'todas las sucursales'}`,
    });
  };

  // Filter appointments by selected branch
  const branchFilteredAppointments = selectedBranch 
    ? appointments.filter(apt => apt.branchId === selectedBranch)
    : appointments;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleUpgradeToPremium = async (packageId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/create-checkout-session?package_id=${packageId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('notimax_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        toast({
          title: 'Error',
          description: 'No se pudo iniciar el proceso de pago',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: 'Error',
        description: 'Error al procesar la solicitud',
        variant: 'destructive',
      });
    }
  };

  const getBannerColor = () => {
    if (!daysRemaining) return 'from-blue-500 to-blue-600';
    if (daysRemaining > 7) return 'from-green-500 to-green-600';
    if (daysRemaining > 3) return 'from-yellow-500 to-yellow-600';
    return 'from-red-500 to-red-600';
  };

  const handleAddAppointment = () => {
    setEditingAppointment(null);
    setShowForm(true);
  };

  const handleEditAppointment = (appointment) => {
    setEditingAppointment(appointment);
    setShowForm(true);
  };

  const handleDeleteAppointment = async (id) => {
    try {
      await appointmentsAPI.delete(id);
      await loadAppointments();
      toast({
        title: 'Cita eliminada',
        description: 'La cita se eliminó correctamente',
      });
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la cita',
        variant: 'destructive',
      });
    }
  };

  const handleSaveAppointment = async (appointment) => {
    try {
      console.log('[Dashboard handleSaveAppointment] Received appointment:', appointment);
      console.log('[Dashboard handleSaveAppointment] appointment.id:', appointment.id);
      console.log('[Dashboard handleSaveAppointment] appointment._id:', appointment._id);
      
      let createdAppointment = null;
      
      if (appointment.id || appointment._id) {
        // Update existing appointment
        console.log('[Dashboard handleSaveAppointment] UPDATING existing appointment with ID:', appointment.id || appointment._id);
        await appointmentsAPI.update(appointment.id || appointment._id, appointment);
        toast({
          title: 'Cita actualizada',
          description: 'Los cambios se guardaron correctamente',
        });
      } else {
        // Create new appointment
        console.log('[Dashboard handleSaveAppointment] CREATING new appointment');
        const response = await appointmentsAPI.create(appointment);
        createdAppointment = response.data;
        
        // Show confirmation dialog instead of toast
        setNewlyCreatedAppointment({...appointment, ...createdAppointment});
        setShowConfirmationDialog(true);
      }
      await loadAppointments();
      setShowForm(false);
    } catch (error) {
      console.error('Error saving appointment:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar la cita',
        variant: 'destructive',
      });
    }
  };

  const sendWhatsAppReminder = (appointment) => {
    setSelectedAppointmentForWA(appointment);
    setShowWhatsAppEditor(true);
  };

  const sendMassReminders = () => {
    setShowMassReminderDialog(true);
  };

  // Format date from calendar to yyyy-MM-dd (handling timezone issues)
  const formatDateToString = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const confirmMassReminders = () => {
    if (!selectedDateForReminders) {
      toast({
        title: 'Error',
        description: 'Por favor selecciona una fecha',
        variant: 'destructive',
      });
      return;
    }

    // Get appointments for the selected date
    const selectedDateStr = formatDateToString(selectedDateForReminders);
    let appointmentsForDate = appointments.filter(apt => apt.date === selectedDateStr);

    // Filter by branch if option is selected
    if (onlySelectedBranch && selectedBranch) {
      appointmentsForDate = appointmentsForDate.filter(apt => apt.branchId === selectedBranch);
    }

    if (appointmentsForDate.length === 0) {
      toast({
        title: 'No hay citas',
        description: onlySelectedBranch && selectedBranch 
          ? 'No hay citas para este día en la sucursal seleccionada'
          : 'No hay citas para este día',
        variant: 'destructive',
      });
      return;
    }

    setShowMassReminderDialog(false);

    // Send to each appointment with a delay between each one
    appointmentsForDate.forEach((appointment, index) => {
      setTimeout(() => {
        sendWhatsAppReminderDirect(appointment);
      }, index * 3000); // 3 seconds delay between each message
    });

    toast({
      title: 'Recordatorios en proceso',
      description: `Enviando recordatorios a ${appointmentsForDate.length} paciente(s)...`,
    });
  };

  const handleSendPendingReminders = () => {
    if (pendingReminders.length === 0) return;

    const confirmed = window.confirm(
      `¿Deseas enviar ${pendingReminders.length} recordatorio(s) pendiente(s)?\n\nSe abrirán múltiples ventanas de WhatsApp.`
    );

    if (!confirmed) return;

    setShowPendingRemindersDialog(false);

    // Send to each appointment with a delay
    pendingReminders.forEach((appointment, index) => {
      setTimeout(() => {
        sendWhatsAppReminderDirect(appointment);
      }, index * 3000); // 3 seconds delay between each message
    });

    toast({
      title: 'Recordatorios enviados',
      description: `Se enviaron ${pendingReminders.length} recordatorio(s)`,
    });

    // Clear pending reminders after sending
    clearPendingReminders();
  };

  const shortenUrl = async (longUrl) => {
    try {
      const response = await fetch(`https://is.gd/create.php?format=json&url=${encodeURIComponent(longUrl)}`);
      const data = await response.json();
      return data.shorturl || longUrl;
    } catch (error) {
      console.error('Error shortening URL:', error);
      return longUrl;
    }
  };

  const sendWhatsAppReminderDirect = async (appointment) => {
    try {
      // Generate links
      const appointmentId = appointment._id || appointment.id;
      const baseUrl = window.location.origin;
      const encodedId = btoa(appointmentId).replace(/=/g, '');
      const confirmUrl = `${baseUrl}/c?i=${encodedId}&a=1`;
      const cancelUrl = `${baseUrl}/c?i=${encodedId}&a=0`;

      // Shorten URLs
      const shortConfirmUrl = await shortenUrl(confirmUrl);
      const shortCancelUrl = await shortenUrl(cancelUrl);

      // Format time to 12-hour
      const [hours, minutes] = appointment.time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      const timeFormatted = `${hour12}:${minutes} ${ampm}`;

      // Create message
      const firstName = appointment.patientName.split(' ')[0];
      const appointmentDate = new Date(appointment.date + 'T00:00:00'); // Force local timezone
      const dayName = format(appointmentDate, 'EEEE', { locale: es }).toUpperCase();
      const dateFormatted = `${dayName} ${format(appointmentDate, 'd')}`;

      // Get service details
      const appointmentServiceName = appointment.serviceType || 'Consulta';
      const serviceDetails = services.find(s => s.name === appointmentServiceName);
      
      let serviceInfo = `📋 ${appointmentServiceName}`;
      if (serviceDetails) {
        if (serviceDetails.duration) {
          serviceInfo += ` (${serviceDetails.duration} min)`;
        }
        if (serviceDetails.description) {
          serviceInfo += `\n   ${serviceDetails.description}`;
        }
      }

      let message = `¡Hola ${firstName}! 👋\n\n`;
      message += `Te recordamos tu cita:\n\n`;
      message += `📅 ${dateFormatted}\n`;
      message += `🕐 ${timeFormatted}\n`;
      message += `${serviceInfo}\n\n`;
      message += `Por favor confirma tu asistencia:\n\n`;
      message += `┌──────────────┐\n`;
      message += `│ ✅ CONFIRMAR │ → ${shortConfirmUrl}\n`;
      message += `└──────────────┘\n\n`;
      message += `┌──────────────┐\n`;
      message += `│ ❌ CANCELAR  │ → ${shortCancelUrl}\n`;
      message += `└──────────────┘\n\n`;
      message += `¡Te esperamos!`;

      const phoneNumber = appointment.phone.replace(/\s+/g, '').replace(/\+/g, '');
      const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    } catch (error) {
      console.error('Error sending WhatsApp reminder:', error);
    }
  };

  const sendConfirmationFromCard = async (appointment) => {
    try {
      console.log('[sendConfirmationFromCard] Starting with appointment:', appointment);
      
      // Load profile settings from API
      let profile = {};
      try {
        const response = await profileAPI.get();
        profile = response.data;
        console.log('[sendConfirmationFromCard] Profile loaded:', profile);
        console.log('[sendConfirmationFromCard] confirmationIncludeService:', profile.confirmationIncludeService);
        console.log('[sendConfirmationFromCard] confirmationIncludeAddress:', profile.confirmationIncludeAddress);
      } catch (error) {
        console.error('Error loading profile:', error);
      }
      
      // Get confirmation template
      let template = profile.confirmationTemplate || '¡Hola {nombre}! 👋\n\nTu cita ha sido agendada exitosamente:\n\n📅 Fecha: {fecha}\n🕐 Hora: {hora}\n📋 Servicio: {servicio}\n{servicio_duracion}\n{servicio_descripcion}\n\n{negocio_especialidad}\n\n{direccion}\n\n{recordatorio_nota}\n\n¡Te esperamos!';
      
      // Format time to 12-hour
      const [hours, minutes] = appointment.time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      const timeFormatted = `${hour12}:${minutes} ${ampm}`;
      
      // Format date with day of week
      const appointmentDate = new Date(appointment.date + 'T00:00:00');
      const dayName = format(appointmentDate, 'EEEE', { locale: es }).toUpperCase();
      const dateFormatted = `${dayName} ${format(appointmentDate, 'd')}`;
      
      // Replace variables
      const firstName = appointment.patientName.split(' ')[0];
      template = template.replace('{nombre}', firstName);
      template = template.replace('{fecha}', dateFormatted);
      template = template.replace('{hora}', timeFormatted);
      
      // Service details
      let serviceText = '';
      let serviceDuration = '';
      let serviceDescription = '';
      
      console.log('[sendConfirmationFromCard] Checking service inclusion...');
      console.log('[sendConfirmationFromCard] profile.confirmationIncludeService:', profile.confirmationIncludeService);
      console.log('[sendConfirmationFromCard] Check !== false:', profile.confirmationIncludeService !== false);
      
      if (profile.confirmationIncludeService !== false) {
        const appointmentServiceName = appointment.serviceType || 'Consulta';
        console.log('[sendConfirmationFromCard] Including service:', appointmentServiceName);
        
        const serviceDetails = services.find(s => s.name === appointmentServiceName);
        
        if (serviceDetails) {
          serviceText = serviceDetails.name;
          serviceDuration = serviceDetails.duration ? `⏱️ Duración: ${serviceDetails.duration}` : '';
          serviceDescription = serviceDetails.description || '';
          console.log('[sendConfirmationFromCard] Found service details:', serviceDetails);
        } else {
          serviceText = appointmentServiceName;
          console.log('[sendConfirmationFromCard] Using appointment service name:', appointmentServiceName);
        }
      } else {
        console.log('[sendConfirmationFromCard] NOT including service (flag is false)');
      }
      
      console.log('[sendConfirmationFromCard] Final serviceText:', serviceText);
      template = template.replace('{servicio}', serviceText);
      template = template.replace('{servicio_duracion}', serviceDuration);
      template = template.replace('{servicio_descripcion}', serviceDescription);
      
      // Business name and specialty
      let businessSpecialtyText = '';
      if (profile.confirmationIncludeBusiness !== false && profile.businessName) {
        businessSpecialtyText += `📍 ${profile.businessName}`;
      }
      if (profile.confirmationIncludeSpecialty !== false && profile.speciality) {
        if (businessSpecialtyText) {
          businessSpecialtyText += ` - ${profile.speciality}`;
        } else {
          businessSpecialtyText = `👨‍⚕️ ${profile.speciality}`;
        }
      }
      template = template.replace('{negocio_especialidad}', businessSpecialtyText);
      
      // Address
      let addressText = '';
      console.log('[sendConfirmationFromCard] Checking address inclusion...');
      console.log('[sendConfirmationFromCard] profile.confirmationIncludeAddress:', profile.confirmationIncludeAddress);
      console.log('[sendConfirmationFromCard] appointment.branchId:', appointment.branchId);
      
      if (profile.confirmationIncludeAddress !== false && appointment.branchId) {
        const branch = branches.find(b => b.id === appointment.branchId);
        console.log('[sendConfirmationFromCard] Found branch:', branch);
        
        if (branch) {
          addressText = `📍 Ubicación: ${branch.name}`;
          if (branch.address) {
            addressText += `\n${branch.address}`;
          }
          // Add Google Maps link if GPS location is available
          if (branch.gpsLocation) {
            addressText += `\n🗺️ Ver en mapa: https://www.google.com/maps?q=${encodeURIComponent(branch.gpsLocation)}`;
          }
        }
      }
      
      console.log('[sendConfirmationFromCard] Final addressText:', addressText);
      template = template.replace('{direccion}', addressText);
      
      // Reminder note
      let reminderNoteText = '';
      if (profile.confirmationIncludeReminderNote !== false) {
        reminderNoteText = '📬 Recibirás un recordatorio antes de tu cita.';
      }
      template = template.replace('{recordatorio_nota}', reminderNoteText);
      
      // Clean up multiple empty lines
      template = template.replace(/\n\n\n+/g, '\n\n').trim();
      
      console.log('[sendConfirmationFromCard] Final message template:');
      console.log(template);
      
      // Send WhatsApp Business
      const phoneNumber = appointment.phone.replace(/\s+/g, '').replace(/\+/g, '');
      const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(template)}`;
      window.open(whatsappUrl, '_blank');
      
      toast({
        title: 'Confirmación enviada',
        description: 'El mensaje de confirmación se abrió en WhatsApp Business',
      });
    } catch (error) {
      console.error('Error sending confirmation:', error);
      toast({
        title: 'Error',
        description: 'No se pudo enviar la confirmación',
        variant: 'destructive',
      });
    }
  };

  const handleSaveTags = async (appointmentId, tagIds) => {
    try {
      // Get the current appointment
      const appointment = appointments.find(apt => apt._id === appointmentId || apt.id === appointmentId);
      if (!appointment) return;
      
      // Update appointment with new tags
      await appointmentsAPI.update(appointmentId, { ...appointment, tags: tagIds });
      
      // Reload appointments
      await loadAppointments();
      
      toast({
        title: 'Etiquetas actualizadas',
        description: 'Las etiquetas se asignaron correctamente',
      });
    } catch (error) {
      console.error('Error saving tags:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron guardar las etiquetas',
        variant: 'destructive',
      });
    }
  };

  const sendThanksMessage = async (appointment) => {
    try {
      // Load profile to get thanks template
      const response = await profileAPI.get();
      const profile = response.data;
      
      let message = profile.thanksTemplate || '¡Gracias por tu visita, {nombre}! 🙏\n\nEsperamos que hayas tenido una excelente experiencia.\n\n¡Esperamos verte pronto!';
      
      // Replace patient name (only first name)
      const firstName = appointment.patientName.split(' ')[0];
      message = message.replace('{nombre}', firstName);
      
      // Social links
      let socialText = '';
      if (profile.thanksIncludeSocial && profile.socialLinks) {
        const links = [];
        if (profile.socialLinks.facebook) links.push(`📘 Facebook: ${profile.socialLinks.facebook}`);
        if (profile.socialLinks.instagram) links.push(`📸 Instagram: ${profile.socialLinks.instagram}`);
        if (profile.socialLinks.whatsapp) links.push(`💬 WhatsApp: ${profile.socialLinks.whatsapp}`);
        if (links.length > 0) {
          socialText = '🌐 Síguenos en redes sociales:\n' + links.join('\n');
        }
      }
      message = message.replace('{redes_sociales}', socialText);
      
      // Review link
      let reviewText = '';
      if (profile.thanksIncludeReview && profile.reviewLink) {
        reviewText = `⭐ Ayúdanos con tu opinión:\n${profile.reviewLink}`;
      }
      message = message.replace('{link_calificacion}', reviewText);
      
      // Clean up multiple empty lines
      message = message.replace(/\n\n\n+/g, '\n\n').trim();
      
      // Open WhatsApp
      const phoneNumber = appointment.phone.replace(/\s+/g, '').replace(/\+/g, '');
      const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
      toast({
        title: 'Mensaje de agradecimiento',
        description: 'El mensaje se abrió en WhatsApp Business',
      });
    } catch (error) {
      console.error('Error sending thanks message:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar la plantilla de agradecimiento',
        variant: 'destructive',
      });
    }
  };

  const sendConfirmationMessage = async () => {
    if (!newlyCreatedAppointment) return;

    try {
      // Load profile settings from API
      let profile = {};
      try {
        const response = await profileAPI.get();
        profile = response.data;
        console.log('[Dashboard] Profile loaded for confirmation:', profile);
        console.log('[Dashboard] confirmationIncludeService:', profile.confirmationIncludeService);
        console.log('[Dashboard] confirmationIncludeBusiness:', profile.confirmationIncludeBusiness);
        console.log('[Dashboard] confirmationIncludeAddress:', profile.confirmationIncludeAddress);
      } catch (error) {
        console.error('Error loading profile:', error);
      }
      
      // Get confirmation template
      let template = profile.confirmationTemplate || '¡Hola {nombre}! 👋\n\nTu cita ha sido agendada exitosamente:\n\n📅 Fecha: {fecha}\n🕐 Hora: {hora}\n📋 Servicio: {servicio}\n{servicio_duracion}\n{servicio_descripcion}\n\n{negocio_especialidad}\n\n{direccion}\n\n{recordatorio_nota}\n\n¡Te esperamos!';
      console.log('[Dashboard] Using template:', template.substring(0, 100) + '...');
      
      // Format time to 12-hour
      const [hours, minutes] = newlyCreatedAppointment.time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      const timeFormatted = `${hour12}:${minutes} ${ampm}`;
      
      // Format date with day of week
      const appointmentDate = new Date(newlyCreatedAppointment.date + 'T00:00:00'); // Force local timezone
      const dayName = format(appointmentDate, 'EEEE', { locale: es }).toUpperCase();
      const dateFormatted = `${dayName} ${format(appointmentDate, 'd')}`;
      
      // Replace variables
      const firstName = newlyCreatedAppointment.patientName.split(' ')[0];
      template = template.replace('{nombre}', firstName);
      template = template.replace('{fecha}', dateFormatted);
      template = template.replace('{hora}', timeFormatted);
      
      // Service type and details (from services list)
      let serviceText = '';
      let serviceDuration = '';
      let serviceDescription = '';
      
      console.log('[Dashboard] Checking confirmationIncludeService:', profile.confirmationIncludeService, '!== false?', profile.confirmationIncludeService !== false);
      
      if (profile.confirmationIncludeService !== false) {
        const appointmentServiceName = newlyCreatedAppointment.serviceType || 'Consulta';
        console.log('[Dashboard] Including service:', appointmentServiceName);
        
        // Try to find the service in the services list to get full details
        const serviceDetails = services.find(s => s.name === appointmentServiceName);
        
        if (serviceDetails) {
          serviceText = serviceDetails.name;
          serviceDuration = serviceDetails.duration ? `⏱️ Duración: ${serviceDetails.duration}` : '';
          serviceDescription = serviceDetails.description || '';
          console.log('[Dashboard] Found service details:', serviceDetails);
        } else {
          serviceText = appointmentServiceName;
          console.log('[Dashboard] Using appointment service name:', serviceText);
        }
      } else {
        console.log('[Dashboard] NOT including service (flag is false)');
      }
      
      console.log('[Dashboard] Final serviceText:', serviceText);
      template = template.replace('{servicio}', serviceText);
      template = template.replace('{servicio_duracion}', serviceDuration);
      template = template.replace('{servicio_descripcion}', serviceDescription);
      
      // Business name and specialty
      let businessSpecialtyText = '';
      if (profile.confirmationIncludeBusiness !== false && profile.businessName) {
        businessSpecialtyText += `📍 ${profile.businessName}`;
      }
      if (profile.confirmationIncludeSpecialty !== false && profile.speciality) {
        if (businessSpecialtyText) {
          businessSpecialtyText += ` - ${profile.speciality}`;
        } else {
          businessSpecialtyText = `👨‍⚕️ ${profile.speciality}`;
        }
      }
      template = template.replace('{negocio_especialidad}', businessSpecialtyText);
      
      // Address
      let addressText = '';
      if (profile.confirmationIncludeAddress !== false && newlyCreatedAppointment.branchId) {
        const branch = branches.find(b => b.id === newlyCreatedAppointment.branchId);
        if (branch) {
          addressText = `📍 Ubicación: ${branch.name}`;
          if (branch.address) {
            addressText += `\n${branch.address}`;
          }
          // Add Google Maps link if GPS location is available
          if (branch.gpsLocation) {
            addressText += `\n🗺️ Ver en mapa: https://www.google.com/maps?q=${encodeURIComponent(branch.gpsLocation)}`;
          }
        }
      }
      template = template.replace('{direccion}', addressText);
      
      // Reminder note
      let reminderNoteText = '';
      if (profile.confirmationIncludeReminderNote !== false) {
        reminderNoteText = '📬 Recibirás un recordatorio antes de tu cita.';
      }
      template = template.replace('{recordatorio_nota}', reminderNoteText);
      
      // Clean up multiple empty lines
      template = template.replace(/\n\n\n+/g, '\n\n').trim();
      
      // Send WhatsApp Business
      const phoneNumber = newlyCreatedAppointment.phone.replace(/\s+/g, '').replace(/\+/g, '');
      const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(template)}`;
      window.open(whatsappUrl, '_blank');
      
      // Close dialog
      setShowConfirmationDialog(false);
      
      toast({
        title: 'Confirmación enviada',
        description: 'El mensaje de confirmación se abrió en WhatsApp Business',
      });
    } catch (error) {
      console.error('Error sending confirmation:', error);
      toast({
        title: 'Error',
        description: 'No se pudo enviar la confirmación',
        variant: 'destructive',
      });
    }
  };

  const saveContactToPhone = (appointment) => {
    try {
      // Create vCard format
      const vCard = `BEGIN:VCARD
VERSION:3.0
FN:${appointment.patientName}
TEL;TYPE=CELL:${appointment.phone}
END:VCARD`;

      // Create blob and download
      const blob = new Blob([vCard], { type: 'text/vcard' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${appointment.patientName.replace(/\s+/g, '_')}.vcf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: 'Contacto descargado',
        description: 'Abre el archivo para guardar el contacto en tu teléfono',
      });
    } catch (error) {
      console.error('Error saving contact:', error);
      toast({
        title: 'Error',
        description: 'No se pudo crear el contacto',
        variant: 'destructive',
      });
    }
  };

  const toggleCardExpansion = (appointmentId) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(appointmentId)) {
        newSet.delete(appointmentId);
      } else {
        newSet.add(appointmentId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const formatTimeTo12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Get dates that have appointments for calendar highlighting
  const getDatesWithAppointments = () => {
    return appointments.map(apt => {
      const dateStr = apt.date.includes('T') ? apt.date.split('T')[0] : apt.date;
      return new Date(dateStr + 'T12:00:00');
    });
  };

  const calendarModifiers = {
    hasAppointment: getDatesWithAppointments()
  };

  const calendarModifiersStyles = {
    hasAppointment: {
      fontWeight: 'bold',
      background: 'linear-gradient(135deg, #3b82f6 0%, #10b981 100%)',
      color: 'white',
      borderRadius: '0.375rem',
    }
  };

  const getStatusBadge = (status) => {
    const statusObj = APPOINTMENT_STATUSES.find(s => s.value === status);
    if (!statusObj) return <Badge className="bg-gray-100 text-gray-800">Desconocido</Badge>;
    return <Badge className={statusObj.color}>{statusObj.label}</Badge>;
  };

  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      await appointmentsAPI.updateStatus(appointmentId, newStatus);
      await loadAppointments();
      
      const statusLabel = APPOINTMENT_STATUSES.find(s => s.value === newStatus)?.label || newStatus;
      toast({
        title: 'Estado actualizado',
        description: `La cita ahora está marcada como "${statusLabel}"`,
      });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el estado',
        variant: 'destructive',
      });
    }
  };

  // Helper function to get today's date in local timezone
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Filter by today's date and status
  const filteredAppointments = branchFilteredAppointments.filter(apt => {
    const today = getTodayDate();
    
    // Only show today's appointments
    if (apt.date !== today) return false;
    
    // Apply status filter
    if (filter === 'all') return true;
    
    // For cancelled filter, include all types of cancellations
    if (filter === 'cancelled') {
      return apt.status === 'cancelled' || apt.status === 'cancelled_patient';
    }
    
    return apt.status === filter;
  });

  // Get count for today
  const todayAppointments = branchFilteredAppointments.filter(apt => {
    const today = getTodayDate();
    return apt.date === today;
  });

  if (showProfile) {
    return <UserProfile onClose={() => { setShowProfile(false); loadUserProfile(); loadBranches(); }} />;
  }

  if (showRecommendations && selectedAppointmentForRec) {
    return (
      <RecommendationsManager
        appointment={selectedAppointmentForRec}
        onClose={() => {
          setShowRecommendations(false);
          setSelectedAppointmentForRec(null);
        }}
      />
    );
  }

  return (
    <>
      {/* Show loading screen while checking auth */}
      {loading && (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-green-500 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-white" />
            </div>
            <p className="text-gray-600">Cargando...</p>
          </div>
        </div>
      )}
      
      {/* Appointment Form - shown/hidden with display */}
      {showForm ? (
        <AppointmentForm
          key={editingAppointment?._id || editingAppointment?.id || 'new-appointment'}
          appointment={editingAppointment}
          onSave={handleSaveAppointment}
          onCancel={() => setShowForm(false)}
        />
      ) : null}
      
      {/* Dashboard - shown when form is hidden */}
      {!showForm && !loading ? (
        <div className="overflow-x-hidden">
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center space-x-2 min-w-0 flex-shrink">
              <img 
                src="/logo.png" 
                alt="NotiMax" 
                className="w-10 h-10 rounded-xl shadow-md object-cover flex-shrink-0"
              />
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 truncate">NotiMax</h1>
                <p className="text-xs text-gray-600 truncate">
                  {userProfile?.businessName || 'Sistema de gestión'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
              <NotificationBell appointments={appointments} />
              {currentUser?.role === 'admin' && (
                <Button
                  onClick={() => navigate('/admin')}
                  variant="outline"
                  className="flex items-center space-x-1 bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-300"
                  size="sm"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline text-sm">Admin</span>
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => setShowProfile(true)}
                className="flex items-center space-x-1 hover:bg-blue-50 min-w-0 p-1 sm:p-2"
                size="sm"
              >
                <Avatar className="w-7 h-7 flex-shrink-0">
                  {userProfile?.photoUrl ? (
                    <AvatarImage src={userProfile.photoUrl} alt="Profile" />
                  ) : (
                    <AvatarFallback className="bg-gradient-to-br from-blue-500 to-green-500 text-white text-xs">
                      {getUserInitials()}
                    </AvatarFallback>
                  )}
                </Avatar>
                <span className="text-xs font-medium truncate hidden sm:inline max-w-[100px]">{userProfile?.name || currentUser?.name}</span>
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="flex items-center space-x-1 hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-all duration-300 border-red-200 text-red-600 flex-shrink-0"
                title="Cerrar Sesión"
                size="sm"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden lg:inline text-sm">Cerrar Sesión</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Trial/Premium Banner */}
      {shouldShowBanner() && (
        <div className={`bg-gradient-to-r ${getBannerColor()} text-white shadow-lg`}>
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-3 rounded-full">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">
                    {daysRemaining > 0 
                      ? `¡Te quedan ${daysRemaining} día${daysRemaining !== 1 ? 's' : ''} de prueba gratis!`
                      : '¡Tu prueba gratuita ha expirado!'}
                  </h3>
                  <p className="text-sm text-white/90">
                    Actualiza a Premium y desbloquea todas las funciones
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setShowUpgradeModal(true)}
                className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-6 py-2 shadow-lg"
              >
                Ver Planes Premium
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Expiring Soon Banner */}
      {subscriptionStatus && subscriptionStatus.is_premium && daysRemaining !== null && daysRemaining < 30 && daysRemaining > 0 && (
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5" />
                <p className="text-sm font-medium">
                  Tu plan premium vence en {daysRemaining} día{daysRemaining !== 1 ? 's' : ''}. Renueva ahora para seguir disfrutando de todos los beneficios.
                </p>
              </div>
              <Button
                onClick={() => setShowUpgradeModal(true)}
                variant="outline"
                className="bg-transparent border-white text-white hover:bg-white/10 text-sm"
                size="sm"
              >
                Renovar Plan
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-6">
        <div className="mb-6">
          <PatientSearch onSelectAppointment={handleEditAppointment} />
        </div>

        <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Citas de Hoy
            </h2>
            <p className="text-gray-600">
              {format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })}
            </p>
          </div>

          {/* Branch Selector */}
          {branches.length > 0 && (
            <div className="w-full sm:w-auto branch-selector-mobile sm:branch-selector-tablet">
              <Select
                value={selectedBranch || 'all'}
                onValueChange={(value) => handleBranchChange(value === 'all' ? null : value)}
              >
                <SelectTrigger className="w-full sm:w-[280px] !bg-white dark:!bg-gray-800 !text-gray-900 dark:!text-gray-100 border-2 border-blue-300 dark:border-blue-600 min-h-[44px] [&>span]:!text-gray-900 dark:[&>span]:!text-gray-100">
                  <SelectValue placeholder="Todas las sucursales" />
                </SelectTrigger>
                <SelectContent className="!bg-white dark:!bg-gray-800 border dark:border-gray-700">
                  <SelectItem value="all" className="!text-gray-900 dark:!text-gray-100 focus:!bg-gray-100 dark:focus:!bg-gray-700">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Todas las sucursales
                    </div>
                  </SelectItem>
                  {branches.map(branch => (
                    <SelectItem key={branch.id} value={branch.id} className="!text-gray-900 dark:!text-gray-100 focus:!bg-gray-100 dark:focus:!bg-gray-700">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{backgroundColor: branch.color}}
                        />
                        {branch.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="mb-6 flex flex-col gap-4">
          <div className="flex flex-wrap gap-2 items-center overflow-x-auto pb-2">
            <Button
              onClick={() => setFilter('all')}
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              className={`whitespace-nowrap ${filter === 'all' ? 'bg-gradient-to-r from-blue-500 to-green-500 text-white' : ''}`}
            >
              Todas ({todayAppointments.length})
            </Button>
            <Button
              onClick={() => setFilter('pending')}
              variant={filter === 'pending' ? 'default' : 'outline'}
              size="sm"
              className={`whitespace-nowrap ${filter === 'pending' ? 'bg-blue-500 text-white' : ''}`}
            >
              Pendientes ({todayAppointments.filter(a => a.status === 'pending').length})
            </Button>
            <Button
              onClick={() => setFilter('confirmed')}
              variant={filter === 'confirmed' ? 'default' : 'outline'}
              size="sm"
              className={`whitespace-nowrap ${filter === 'confirmed' ? 'bg-green-500 text-white' : ''}`}
            >
              Confirmadas ({todayAppointments.filter(a => a.status === 'confirmed').length})
            </Button>
            <Button
              onClick={() => setFilter('cancelled')}
              variant={filter === 'cancelled' ? 'default' : 'outline'}
              size="sm"
              className={`whitespace-nowrap ${filter === 'cancelled' ? 'bg-red-500 text-white' : ''}`}
            >
              Canceladas ({todayAppointments.filter(a => a.status === 'cancelled' || a.status === 'cancelled_patient').length})
            </Button>
            <Button
              onClick={loadAppointments}
              variant="outline"
              size="sm"
              title="Recargar citas"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 w-full">
            {pendingReminders.length > 0 && (
              <Button
                onClick={() => setShowPendingRemindersDialog(true)}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 relative w-full md:w-auto"
              >
                <Bell className="w-4 h-4 mr-2 animate-pulse" />
                <span className="hidden md:inline">Recordatorios Pendientes</span>
                <span className="md:hidden">Recordatorios</span>
                <Badge className="ml-2 bg-white text-orange-600 font-bold">
                  {pendingReminders.length}
                </Badge>
              </Button>
            )}
            <Button
              onClick={() => setShowTagsManager(true)}
              variant="outline"
              className="hover:bg-purple-50 hover:text-purple-600 hover:border-purple-300 transition-all duration-300 w-full md:w-auto min-h-[44px]"
            >
              <User className="w-4 h-4 mr-2" />
              Etiquetas
            </Button>
            <Button
              onClick={() => navigate('/statistics')}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-medium shadow-lg hover:shadow-xl transition-all duration-300 w-full md:w-auto min-h-[44px]"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              📊 Estadísticas
            </Button>
            <Button
              onClick={() => navigate('/calendar')}
              variant="outline"
              className="hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300 transition-all duration-300 w-full md:w-auto min-h-[44px]"
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              Vista Calendario
            </Button>
            <Button
              onClick={handleAddAppointment}
              className="bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white font-medium shadow-lg hover:shadow-xl transition-all duration-300 w-full md:w-auto min-h-[44px]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Cita
            </Button>
          </div>
        </div>

        {filteredAppointments.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 text-lg">No hay citas registradas</p>
              <Button onClick={handleAddAppointment} className="mt-4 bg-gradient-to-r from-blue-500 to-green-500 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Crear primera cita
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredAppointments.map((appointment) => {
              const aptId = appointment._id || appointment.id;
              const isExpanded = expandedCards.has(aptId);
              
              return (
              <Card key={aptId} className="hover:shadow-lg transition-all duration-300 border-l-4 border-l-blue-500">
                <CardHeader className="pb-2 pt-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <CardTitle className="text-base font-semibold text-gray-900">
                          {appointment.patientName}
                        </CardTitle>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleCardExpansion(aptId)}
                          className="ml-2 h-6 w-6 p-0"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 items-center text-xs">
                        {getStatusBadge(appointment.status)}
                        {appointment.serviceType && (
                          <Badge className="bg-blue-600 text-white text-xs py-0 px-2">
                            {appointment.serviceType}
                          </Badge>
                        )}
                        {appointment.tags && appointment.tags.length > 0 && (
                          appointment.tags.map(tagId => {
                            const tag = availableTags.find(t => t.id === tagId);
                            if (!tag) return null;
                            return (
                              <Badge 
                                key={tagId}
                                className="text-white text-xs py-0 px-2"
                                style={{ backgroundColor: tag.color }}
                              >
                                {tag.name}
                              </Badge>
                            );
                          })
                        )}
                        <div className="flex items-center text-xs text-gray-600">
                          <Clock className="w-3 h-3 mr-1 text-blue-500" />
                          {formatTimeTo12Hour(appointment.time)}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-2 py-2">
                  {isExpanded && (
                  <>
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="w-4 h-4 mr-2 text-blue-500" />
                    {appointment.phone}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-2 text-green-500" />
                    {formatDate(appointment.date)}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Clock className="w-4 h-4 mr-2 text-blue-500" />
                    {formatTimeTo12Hour(appointment.time)}
                  </div>
                  <div className="pt-2 border-t space-y-2">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Motivo:</p>
                      <p className="text-sm text-gray-900">{appointment.reason}</p>
                    </div>
                    {appointment.reminderTime && (
                      <div className="flex items-center text-xs text-blue-600">
                        <span className="mr-1">🔔</span>
                        <span>
                          Recordatorio: {appointment.reminderTime < 60 
                            ? `${appointment.reminderTime} min antes`
                            : `${appointment.reminderTime / 60} hora${appointment.reminderTime / 60 > 1 ? 's' : ''} antes`}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2 pt-3">
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => {
                          setSelectedAppointmentForRec(appointment);
                          setShowRecommendations(true);
                        }}
                        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                        size="sm"
                        title="Recomendaciones"
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        Notas
                      </Button>
                      <Button
                        onClick={() => sendWhatsAppReminder(appointment)}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                        size="sm"
                      >
                        <Send className="w-3 h-3 mr-1" />
                        Enviar
                      </Button>
                      <Button
                        onClick={() => handleEditAppointment(appointment)}
                        variant="outline"
                        size="sm"
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      {/* Inline Menu (no portals) */}
                      <div className="relative">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setOpenMenuId(openMenuId === aptId ? null : aptId)}
                        >
                          <MoreVertical className="w-3 h-3" />
                        </Button>
                        
                        {openMenuId === aptId && (
                          <>
                            {/* Backdrop */}
                            <div 
                              className="fixed inset-0 z-10" 
                              onClick={() => setOpenMenuId(null)}
                            />
                            {/* Menu - se abre hacia arriba */}
                            <div className="absolute right-0 bottom-full mb-1 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-20 max-h-80 overflow-y-auto">
                              <button
                                onClick={() => { sendConfirmationFromCard(appointment); setOpenMenuId(null); }}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-gray-900 dark:text-gray-100"
                              >
                                <Phone className="w-4 h-4 mr-2 text-blue-600" />
                                <span className="text-sm">Enviar confirmación</span>
                              </button>
                              <button
                                onClick={() => { saveContactToPhone(appointment); setOpenMenuId(null); }}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-gray-900 dark:text-gray-100"
                              >
                                <UserPlus className="w-4 h-4 mr-2 text-green-600" />
                                <span className="text-sm">Guardar contacto</span>
                              </button>
                              <button
                                onClick={() => { sendThanksMessage(appointment); setOpenMenuId(null); }}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-gray-900 dark:text-gray-100"
                              >
                                <Heart className="w-4 h-4 mr-2 text-purple-600" />
                                <span className="text-sm">Enviar agradecimiento</span>
                              </button>
                              <button
                                onClick={() => { 
                                  setSelectedAppointmentForTags(appointment); 
                                  setShowTagSelector(true); 
                                  setOpenMenuId(null); 
                                }}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-gray-900 dark:text-gray-100"
                              >
                                <TagIcon className="w-4 h-4 mr-2 text-blue-600" />
                                <span className="text-sm">Asignar etiquetas</span>
                              </button>
                              <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                              <button
                                onClick={() => { updateAppointmentStatus(aptId, 'confirmed'); setOpenMenuId(null); }}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-gray-900 dark:text-gray-100"
                              >
                                <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                                <span className="text-sm">Marcar confirmada</span>
                              </button>
                              <button
                                onClick={() => { updateAppointmentStatus(aptId, 'completed'); setOpenMenuId(null); }}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-gray-900 dark:text-gray-100"
                              >
                                <CheckCircle className="w-4 h-4 mr-2 text-purple-600" />
                                <span className="text-sm">Marcar completada</span>
                              </button>
                              <button
                                onClick={() => { updateAppointmentStatus(aptId, 'cancelled'); setOpenMenuId(null); }}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-gray-900 dark:text-gray-100"
                              >
                                <XCircle className="w-4 h-4 mr-2 text-red-600" />
                                <span className="text-sm">Cancelar cita</span>
                              </button>
                              <button
                                onClick={() => { updateAppointmentStatus(aptId, 'no_show'); setOpenMenuId(null); }}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-gray-900 dark:text-gray-100"
                              >
                                <AlertCircle className="w-4 h-4 mr-2 text-gray-600" />
                                <span className="text-sm">No asistió</span>
                              </button>
                              <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                              <button
                                onClick={() => { handleDeleteAppointment(aptId); setOpenMenuId(null); }}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                <span className="text-sm">Eliminar</span>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  </>
                  )}
                </CardContent>
              </Card>
            )})}
          </div>
        )}
      </div>

      {/* Modals */}
      {showWhatsAppEditor && selectedAppointmentForWA && (
        <WhatsAppMessageEditor
          appointment={selectedAppointmentForWA}
          onClose={() => {
            setShowWhatsAppEditor(false);
            setSelectedAppointmentForWA(null);
          }}
        />
      )}

      {showTagsManager && (
        <PatientTagsManager
          onClose={() => setShowTagsManager(false)}
        />
      )}

      {showTagSelector && selectedAppointmentForTags && (
        <TagSelector
          appointment={selectedAppointmentForTags}
          onClose={() => {
            setShowTagSelector(false);
            setSelectedAppointmentForTags(null);
          }}
          onSave={(tagIds) => handleSaveTags(selectedAppointmentForTags._id || selectedAppointmentForTags.id, tagIds)}
        />
      )}

      {/* Mass Reminders Date Selector - Simple Modal */}
      {showMassReminderDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-[550px] max-h-[90vh] flex flex-col">
            <div className="p-4 pb-2 border-b">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-semibold">Enviar Recordatorios Masivos</h2>
                  <p className="text-sm text-gray-600">Selecciona la fecha para enviar recordatorios por WhatsApp</p>
                </div>
                <button
                  onClick={() => setShowMassReminderDialog(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="overflow-y-auto p-4 flex-1">
              {/* Branch Filter Option */}
              {selectedBranch && branches.length > 0 && (
                <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-900 text-sm mb-3">Filtrar por sucursal:</h4>
                  <RadioGroup
                    value={onlySelectedBranch ? 'selected' : 'all'}
                    onValueChange={(value) => setOnlySelectedBranch(value === 'selected')}
                    className="space-y-2"
                  >
                    <div className="flex items-center space-x-2 p-2 bg-white rounded hover:bg-blue-50 cursor-pointer">
                      <RadioGroupItem value="selected" id="selected-branch" />
                      <label htmlFor="selected-branch" className="flex-1 cursor-pointer text-sm">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{backgroundColor: branches.find(b => b.id === selectedBranch)?.color}}
                          />
                          <span className="font-medium">
                            Solo {branches.find(b => b.id === selectedBranch)?.name}
                          </span>
                        </div>
                      </label>
                    </div>
                    <div className="flex items-center space-x-2 p-2 bg-white rounded hover:bg-blue-50 cursor-pointer">
                      <RadioGroupItem value="all" id="all-branches" />
                      <label htmlFor="all-branches" className="flex-1 cursor-pointer text-sm">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-600" />
                          <span className="font-medium">Todas las sucursales</span>
                        </div>
                      </label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              <div className="mb-3">
                <CalendarComponent
                  mode="single"
                  selected={selectedDateForReminders}
                  onSelect={setSelectedDateForReminders}
                  locale={es}
                  modifiers={calendarModifiers}
                  modifiersStyles={calendarModifiersStyles}
                  className="rounded-md border shadow-sm mx-auto"
                />
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-900 text-sm mb-1.5">
                  Citas para {selectedDateForReminders ? format(selectedDateForReminders, "d 'de' MMMM 'de' yyyy", { locale: es }) : 'seleccionar fecha'}:
                </h4>
                <div className="text-blue-800">
                  {(() => {
                    if (!selectedDateForReminders) {
                      return <p className="text-xs">Selecciona una fecha del calendario</p>;
                    }
                    
                    const selectedDateStr = formatDateToString(selectedDateForReminders);
                    let appointmentsForDate = appointments.filter(apt => apt.date === selectedDateStr);
                    
                    // Apply branch filter if selected
                    if (onlySelectedBranch && selectedBranch) {
                      appointmentsForDate = appointmentsForDate.filter(apt => apt.branchId === selectedBranch);
                    }
                    
                    if (appointmentsForDate.length === 0) {
                      return <p className="text-xs">
                        {onlySelectedBranch && selectedBranch 
                          ? 'No hay citas para este día en la sucursal seleccionada'
                          : 'No hay citas programadas para este día'}
                      </p>;
                    }
                    
                    return (
                      <div>
                        <p className="font-semibold text-xs mb-1.5">
                          {appointmentsForDate.length} cita(s) encontrada(s)
                          {onlySelectedBranch && selectedBranch && 
                            ` en ${branches.find(b => b.id === selectedBranch)?.name}`
                          }:
                        </p>
                        <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
                          {appointmentsForDate.map((apt) => (
                            <li key={apt._id || apt.id} className="flex items-center">
                              <User className="w-3 h-3 mr-1.5 flex-shrink-0" />
                              <span className="truncate">{apt.patientName} - {formatTimeTo12Hour(apt.time)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 relative z-10">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowMassReminderDialog(false);
                }}
                className="px-4 py-2 text-sm font-medium border border-gray-300 bg-white rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Botón Enviar clickeado');
                  confirmMassReminders();
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 flex items-center gap-1.5"
              >
                <SendHorizontal className="w-4 h-4" />
                Enviar Recordatorios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Reminders Modal */}
      {showPendingRemindersDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-[600px] max-h-[90vh] flex flex-col">
            <div className="p-4 pb-2 border-b bg-gradient-to-r from-orange-500 to-red-500">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                    <Bell className="w-6 h-6 animate-pulse" />
                    Recordatorios Pendientes
                  </h2>
                  <p className="text-sm text-orange-50">Es hora de enviar estos recordatorios</p>
                </div>
                <button
                  onClick={() => setShowPendingRemindersDialog(false)}
                  className="text-white hover:text-orange-100 p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="overflow-y-auto p-4 flex-1">
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-900 font-medium">
                  📱 Se abrirán {pendingReminders.length} ventana(s) de WhatsApp
                </p>
                <p className="text-xs text-orange-700 mt-1">
                  Cada mensaje se abrirá con 3 segundos de diferencia
                </p>
              </div>

              <div className="space-y-2">
                {pendingReminders.map((apt, index) => (
                  <div
                    key={apt._id || apt.id}
                    className="p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-4 h-4 text-blue-600" />
                          <p className="font-semibold text-gray-900">{apt.patientName}</p>
                          <Badge variant="outline" className="text-xs">
                            {apt.reminderType === '24h' ? '24h antes' : 
                             apt.reminderType === '12h' ? '12h antes' : 
                             'Mismo día'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-600 ml-6">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(apt.date), "d 'de' MMMM", { locale: es })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTimeTo12Hour(apt.time)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {apt.phone}
                          </span>
                        </div>
                        {apt.serviceType && (
                          <p className="text-xs text-gray-500 ml-6 mt-1">
                            📋 {apt.serviceType}
                          </p>
                        )}
                      </div>
                      <div className="text-2xl">
                        {index + 1}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowPendingRemindersDialog(false)}
                className="px-4 py-2 text-sm font-medium border border-gray-300 bg-white rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendPendingReminders}
                className="px-5 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-green-600 rounded-md hover:from-green-600 hover:to-green-700 flex items-center gap-2 shadow-lg"
              >
                <SendHorizontal className="w-4 h-4" />
                Enviar Todos los Recordatorios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Message Dialog */}
      {showConfirmationDialog && newlyCreatedAppointment && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-3"
          onClick={() => setShowConfirmationDialog(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-4 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">
                ¡Cita Creada!
              </h3>
              <p className="text-gray-600 text-xs">
                <span className="font-semibold">{newlyCreatedAppointment.patientName}</span> ha sido agendado.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-blue-900 text-xs mb-0.5">
                    ¿Enviar confirmación por WhatsApp?
                  </p>
                  <p className="text-xs text-blue-700">
                    Mensaje de confirmación con detalles de la cita.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                onClick={sendConfirmationMessage}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-5"
                size="sm"
              >
                <Phone className="w-4 h-4 mr-2" />
                Enviar por WhatsApp
              </Button>
              <Button
                onClick={() => {
                  setShowConfirmationDialog(false);
                  toast({
                    title: 'Cita creada',
                    description: 'La cita se creó correctamente',
                  });
                }}
                variant="outline"
                className="w-full"
                size="sm"
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">🎉 Actualiza a Premium</h2>
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <p className="text-gray-600 mb-6">
                Desbloquea todas las funciones premium y lleva tu gestión al siguiente nivel
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Monthly Plan */}
                <div className="border-2 border-blue-200 rounded-lg p-6 hover:border-blue-500 transition-colors">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Premium Mensual</h3>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-blue-600">$14</span>
                      <span className="text-gray-500 ml-2">/mes</span>
                    </div>
                  </div>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Exportación a PDF y Excel</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Exportación de contactos</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Estadísticas avanzadas</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Hasta 3 dispositivos</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Soporte prioritario</span>
                    </li>
                  </ul>
                  <Button
                    onClick={() => handleUpgradeToPremium('premium_monthly')}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    Suscribirse Mensual
                  </Button>
                </div>

                {/* Annual Plan */}
                <div className="border-2 border-green-200 rounded-lg p-6 hover:border-green-500 transition-colors relative">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-green-500 text-white px-3 py-1">¡Ahorra 16%!</Badge>
                  </div>
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Premium Anual</h3>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-green-600">$140</span>
                      <span className="text-gray-500 ml-2">/año</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">Solo $11.67/mes</p>
                  </div>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Exportación a PDF y Excel</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Exportación de contactos</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Estadísticas avanzadas</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Hasta 3 dispositivos</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Soporte prioritario</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600 font-semibold">2 meses gratis</span>
                    </li>
                  </ul>
                  <Button
                    onClick={() => handleUpgradeToPremium('premium_annual')}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    Suscribirse Anual
                  </Button>
                </div>
              </div>

              <div className="text-center text-sm text-gray-500 mt-6">
                <p>💳 Pago seguro procesado por Stripe</p>
                <p className="mt-1">Cancela en cualquier momento sin compromiso</p>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
      ) : null}
    </>
  );
};

export default Dashboard;